import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Loader2, MousePointerClick, GripVertical, Star } from "lucide-react";
import { api, fileUrl, apiError } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const emptyForm = () => ({ text: "", link: "", logo_path: null, is_published: false, is_top: false, notify: true });

export function AdminBookmakers() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [dragIdx, setDragIdx] = useState(null);

  const load = async () => { const { data } = await api.get("/admin/bookmakers"); setItems(data); };
  useEffect(() => { load(); }, []);

  const openCreate = () => { setEditing(null); setForm(emptyForm()); setOpen(true); };
  const openEdit = (b) => { setEditing(b.id); setForm({ text: b.text, link: b.link, logo_path: b.logo_path, is_published: b.is_published, is_top: b.is_top }); setOpen(true); };

  const save = async () => {
    if (!form.text.trim() || !form.link.trim()) return toast.error("Inserisci testo e link.");
    setSaving(true);
    try {
      if (editing) await api.put(`/admin/bookmakers/${editing}`, form);
      else await api.post("/admin/bookmakers", form);
      toast.success(editing ? "Bookmaker aggiornato" : "Bookmaker creato");
      setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const togglePublish = async (b) => {
    try {
      await api.put(`/admin/bookmakers/${b.id}`, { text: b.text, link: b.link, logo_path: b.logo_path, is_published: !b.is_published, is_top: b.is_top });
      toast.success(b.is_published ? "Nascosto" : "Pubblicato");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Eliminare questo bookmaker?")) return;
    await api.delete(`/admin/bookmakers/${id}`);
    toast.success("Eliminato"); load();
  };

  const onDrop = async (idx) => {
    if (dragIdx === null || dragIdx === idx) return;
    const arr = [...items];
    const [m] = arr.splice(dragIdx, 1);
    arr.splice(idx, 0, m);
    setItems(arr); setDragIdx(null);
    try { await api.put("/admin/bookmakers/reorder", { ids: arr.map((x) => x.id) }); toast.success("Ordine aggiornato"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  if (items === null) return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-green-500" /></div>;

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold uppercase text-zinc-950">Bonus / Bookmaker</h2>
        <Button data-testid="admin-create-bookmaker-button" onClick={openCreate} className="rounded-full bg-green-500 font-bold text-zinc-950 hover:bg-green-600">
          <Plus className="mr-1 h-4 w-4" /> Nuovo
        </Button>
      </div>

      <div className="space-y-3">
        {items.length === 0 && <p className="text-sm text-zinc-500">Nessun bookmaker. Creane uno e pubblicalo.</p>}
        {items.length > 0 && <p className="mb-1 text-xs text-zinc-400">Trascina le righe per cambiare l'ordine mostrato agli utenti.</p>}
        {items.map((b, idx) => (
          <div key={b.id} data-testid={`admin-bookmaker-${b.id}`} draggable
            onDragStart={() => setDragIdx(idx)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(idx)}
            className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4">
            <span className="cursor-grab text-zinc-300" title="Trascina per riordinare"><GripVertical className="h-5 w-5" /></span>
            {b.logo_path ? <img src={fileUrl(b.logo_path)} alt="" className="h-12 w-12 rounded-xl object-contain" />
              : <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-zinc-950 font-display text-sm font-black text-green-400">{b.text?.slice(0, 2).toUpperCase()}</div>}
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="truncate font-semibold text-zinc-900">{b.text}</p>
                {b.is_top && <span className="flex shrink-0 items-center gap-1 rounded-full bg-yellow-400 px-2 py-0.5 text-[10px] font-black uppercase text-zinc-950"><Star className="h-3 w-3" />Top</span>}
              </div>
              <p className="truncate text-xs text-zinc-400">{b.link}</p>
              <p className="mt-1 flex items-center gap-1 text-xs font-bold text-green-600"><MousePointerClick className="h-3 w-3" /> {b.clicks} click "PROVA ORA"</p>
            </div>
            <div className="flex shrink-0 flex-col items-end gap-2">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase text-zinc-400">{b.is_published ? "Pubblico" : "Bozza"}</span>
                <Switch data-testid={`admin-bookmaker-publish-${b.id}`} checked={b.is_published} onCheckedChange={() => togglePublish(b)} />
              </div>
              <div className="flex gap-1">
                <Button onClick={() => openEdit(b)} size="sm" variant="ghost"><Pencil className="h-4 w-4" /></Button>
                <Button data-testid={`admin-bookmaker-delete-${b.id}`} onClick={() => del(b.id)} size="sm" variant="ghost" className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-display text-2xl font-black uppercase">{editing ? "Modifica" : "Nuovo"} bookmaker</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <ImageUploader label="Logo bookmaker" value={form.logo_path} onChange={(v) => setForm({ ...form, logo_path: v })} testId="bookmaker-logo" />
            <div className="space-y-1.5">
              <Label>Testo</Label>
              <Textarea data-testid="bookmaker-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} placeholder="Es. SuperBet — Bonus di benvenuto 100% fino a 100€" className="rounded-xl" />
            </div>
            <div className="space-y-1.5">
              <Label>Link di affiliazione</Label>
              <Input data-testid="bookmaker-link-input" value={form.link} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://..." className="rounded-xl" />
            </div>
            <label className="flex items-center justify-between rounded-2xl bg-slate-100 p-3">
              <span className="text-sm font-semibold text-zinc-700">Pubblica subito</span>
              <Switch data-testid="bookmaker-publish-switch" checked={form.is_published} onCheckedChange={(v) => setForm({ ...form, is_published: v })} />
            </label>
            <label className="flex items-center justify-between rounded-2xl bg-yellow-50 p-3">
              <span className="text-sm font-semibold text-zinc-700">⭐ Consigliato da Tony (badge Top)</span>
              <Switch data-testid="bookmaker-top-switch" checked={form.is_top} onCheckedChange={(v) => setForm({ ...form, is_top: v })} />
            </label>
            <label className="flex items-center justify-between rounded-2xl bg-slate-100 p-3">
              <span className="text-sm font-semibold text-zinc-700">Invia notifica push agli utenti</span>
              <Switch data-testid="bookmaker-notify-switch" checked={form.notify !== false} onCheckedChange={(v) => setForm({ ...form, notify: v })} />
            </label>
          </div>
          <DialogFooter>
            <Button data-testid="bookmaker-save-button" onClick={save} disabled={saving} className="w-full rounded-full bg-zinc-950 font-bold text-green-400 hover:bg-zinc-800">
              {saving ? "Salvataggio..." : editing ? "Salva" : "Crea bookmaker"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
